import {
  type BankAccountLevelOverride,
  type CustomRolePermissionLevel,
  type EntityLevelOverride,
  type PlatformLevelPermissions,
} from '@column/api';

import { Access, Permissions } from './types';

export const PlatformLevelPermissionsToLabelsMap: {
  [K in keyof Partial<KeysCamelCaseDeep<PlatformLevelPermissions>>]: string;
} = {
  achCredits: 'ACH Credits',
  achDebits: 'ACH Debits',
  achReturns: 'ACH Returns',
  approveTransfers: 'Approve Transfers',
  bankAccounts: 'Bank Accounts',
  entities: 'Entities',
  wireTransfers: 'Wire Transfers',
  depositChecks: 'Deposit Checks',
  issueChecks: 'Issue Checks',
  bookTransfers: 'Book Transfers',
  realtimeTransfers: 'Realtime Transfers',
  stopPayments: 'Stop Payments',
};
export const BankAccountLevelPermissionsToLabelsMap: {
  [K in keyof Partial<KeysCamelCaseDeep<BankAccountLevelOverride>>]: string;
} = {
  achCredits: 'ACH Credits',
  achDebits: 'ACH Debits',
  achReturns: 'ACH Returns',
  approveTransfers: 'Approve Transfers',
  wireTransfers: 'Wire Transfers',
  depositChecks: 'Deposit Checks',
  issueChecks: 'Issue Checks',
  bookTransfers: 'Book Transfers',
  realtimeTransfers: 'Realtime Transfers',
  stopPayments: 'Stop Payments',
  thisBankAccount: 'This Bank Account',
};

export const BankAccountDefaultPermissions: {
  [K in keyof Partial<KeysCamelCaseDeep<BankAccountLevelOverride>>]: `${CustomRolePermissionLevel}`;
} = {
  achCredits: Access.Default,
  achDebits: Access.Default,
  achReturns: Access.Default,
  approveTransfers: Access.Default,
  wireTransfers: Access.Default,
  depositChecks: Access.Default,
  issueChecks: Access.Default,
  bookTransfers: Access.Default,
  realtimeTransfers: Access.Default,
  stopPayments: Access.Default,
  thisBankAccount: Access.Default,
};

export const EntityLevelPermissionsToLabelsMap: {
  [K in keyof Partial<KeysCamelCaseDeep<EntityLevelOverride>>]: string;
} = {
  achCredits: 'ACH Credits',
  achDebits: 'ACH Debits',
  achReturns: 'ACH Returns',
  approveTransfers: 'Approve Transfers',
  wireTransfers: 'Wire Transfers',
  depositChecks: 'Deposit Checks',
  issueChecks: 'Issue Checks',
  bookTransfers: 'Book Transfers',
  realtimeTransfers: 'Realtime Transfers',
  stopPayments: 'Stop Payments',
  bankAccounts: 'Bank Accounts on Entity',
  thisEntity: 'This Entity',
};

export const EntityLevelDefaultPermissions: {
  [K in keyof Partial<KeysCamelCaseDeep<EntityLevelOverride>>]: `${CustomRolePermissionLevel}`;
} = {
  achCredits: Access.Default,
  achDebits: Access.Default,
  achReturns: Access.Default,
  approveTransfers: Access.Default,
  wireTransfers: Access.Default,
  depositChecks: Access.Default,
  issueChecks: Access.Default,
  bookTransfers: Access.Default,
  realtimeTransfers: Access.Default,
  stopPayments: Access.Default,
  bankAccounts: Access.Default,
  thisEntity: Access.Default,
};

export const buildBankAccountOverridesFromPermissions = (
  bankAccountId: string,
  permissions: Permissions
): Partial<KeysCamelCaseDeep<BankAccountLevelOverride>> =>
  Object.entries(permissions).reduce((o, [permission, value]) => ({ ...o, [permission]: value }), { bankAccountId });

export const buildBankAccountOverridesFromPermissionLevel = (
  bankAccountId: string,
  permissionLevel: `${CustomRolePermissionLevel}`,
  permissionsToLabelsMap = BankAccountLevelPermissionsToLabelsMap
): Partial<KeysCamelCaseDeep<BankAccountLevelOverride>> =>
  Object.keys(permissionsToLabelsMap).reduce((o, permission) => ({ ...o, [permission]: permissionLevel }), {
    bankAccountId,
  });

export const buildEntityOverridesFromPermissions = (
  entityId: string,
  permissions: Permissions
): Partial<KeysCamelCaseDeep<EntityLevelOverride>> =>
  Object.entries(permissions).reduce((o, [permission, value]) => ({ ...o, [permission]: value }), { entityId });

export const buildEntityOverridesFromPermissionLevel = (
  entityId: string,
  permissionLevel: `${CustomRolePermissionLevel}`,
  permissionsToLabelsMap = EntityLevelPermissionsToLabelsMap
): Partial<KeysCamelCaseDeep<EntityLevelOverride>> =>
  Object.keys(permissionsToLabelsMap).reduce((o, permission) => ({ ...o, [permission]: permissionLevel }), {
    entityId,
  });

export const getAccessLevel = (permissions: Permissions): Access => {
  if (Object.values(permissions).every((value) => value === 'default')) {
    return Access.Default;
  }
  if (Object.values(permissions).every((value) => value === 'none')) {
    return Access.NoAccess;
  }
  if (Object.values(permissions).every((value) => value === 'read')) {
    return Access.ViewOnly;
  }
  if (Object.values(permissions).every((value) => value === 'write')) {
    return Access.FullAccess;
  }
  return Access.Custom;
};

export const getBankAccountPermissions = ({
  bankAccountId,
  bankAccountLevelOverrides,
  permissionsToLabelsMap = BankAccountLevelPermissionsToLabelsMap,
}: {
  bankAccountId: string;
  bankAccountLevelOverrides: Partial<KeysCamelCaseDeep<BankAccountLevelOverride>>[];
  permissionsToLabelsMap?: typeof BankAccountLevelPermissionsToLabelsMap;
}): { bankAccountIndex: number; actualPermissions: Permissions; numOverrides: number } => {
  // bankAccountIndex is the index of the bank account in the bankAccountLevelOverrides array
  const bankAccountIndex = bankAccountLevelOverrides.findIndex((override) => override.bankAccountId === bankAccountId);
  const bankAccountOverride = bankAccountIndex !== -1 ? bankAccountLevelOverrides[bankAccountIndex] : undefined;

  // actualPermissions is the list of permission for the bank account, as represented by the API
  const actualPermissions = Object.keys(permissionsToLabelsMap).reduce(
    (o, permission) => ({
      ...o,
      [permission]:
        bankAccountOverride?.[permission as keyof Partial<KeysCamelCaseDeep<BankAccountLevelOverride>>] ?? 'default',
    }),
    {}
  );

  // numOverrides is the number of permissions that are different from the entity level
  const numOverrides = Object.values(actualPermissions).filter((value) => value !== 'default').length;

  return { bankAccountIndex, actualPermissions, numOverrides };
};

export const getEntityPermissions = ({
  entityId,
  entityLevelOverrides,
  permissionsToLabelsMap = EntityLevelPermissionsToLabelsMap,
  platformPermissions,
}: {
  entityId: string;
  entityLevelOverrides: Partial<KeysCamelCaseDeep<EntityLevelOverride>>[];
  permissionsToLabelsMap?: typeof EntityLevelPermissionsToLabelsMap;
  platformPermissions: Partial<KeysCamelCaseDeep<PlatformLevelPermissions>>;
}): {
  entityIndex: number;
  actualPermissions: Permissions;
  effectivePermissions: Permissions;
  numOverrides: number;
} => {
  // entityIndex is the index of the entity in the entityLevelOverrides array
  const entityIndex = entityLevelOverrides.findIndex((override) => override.entityId === entityId);
  const entityOverrides = entityIndex !== -1 ? entityLevelOverrides[entityIndex] : undefined;

  // actualPermissions is the list of permission for the entity, as represented by the API
  const actualPermissions = Object.keys(permissionsToLabelsMap).reduce(
    (o, permission) => ({
      ...o,
      [permission]: entityOverrides?.[permission as keyof Partial<KeysCamelCaseDeep<EntityLevelOverride>>] ?? 'default',
    }),
    {}
  );

  // effectivePermissions is the full computed list of permission for the entity, taking into account parent permissions
  const effectivePermissions = Object.keys(permissionsToLabelsMap).reduce(
    (o, permission) => ({
      ...o,
      [permission]:
        entityOverrides?.[permission as keyof Partial<KeysCamelCaseDeep<EntityLevelOverride>>] &&
        entityOverrides[permission as keyof Partial<KeysCamelCaseDeep<EntityLevelOverride>>] !== 'default'
          ? entityOverrides[permission as keyof Partial<KeysCamelCaseDeep<EntityLevelOverride>>]
          : platformPermissions[permission as keyof Partial<KeysCamelCaseDeep<PlatformLevelPermissions>>],
    }),
    {}
  );

  // numOverrides is the number of permissions that are different from the platform level
  const numOverrides = Object.values(actualPermissions).filter((value) => value !== 'default').length;

  return { entityIndex, actualPermissions, effectivePermissions, numOverrides };
};

export const setBankAccountPermissionsFromPermissions = ({
  permissions,
  keyPrefix,
  setter,
}: {
  permissions: Permissions;
  keyPrefix: string;
  setter: (key: string, value: string) => void;
}) => {
  Object.entries(permissions).forEach(([permission, value]) => {
    setter(`${keyPrefix}${permission}`, value);
  });
};

export const setBankAccountPermissionsFromPermissionLevel = ({
  permissionLevel,
  keyPrefix,
  permissionsToLabelsMap = BankAccountLevelPermissionsToLabelsMap,
  setter,
}: {
  permissionLevel: `${CustomRolePermissionLevel}`;
  keyPrefix: string;
  permissionsToLabelsMap?: typeof BankAccountLevelPermissionsToLabelsMap;
  setter: (key: string, value: string) => void;
}) => {
  Object.keys(permissionsToLabelsMap).forEach((permission) => {
    setter(`${keyPrefix}${permission}`, permissionLevel);
  });
};

export const setEntityPermissionsFromPermissions = ({
  permissions,
  keyPrefix,
  setter,
}: {
  permissions: Permissions;
  keyPrefix: string;
  setter: (key: string, value: string) => void;
}) => {
  Object.entries(permissions).forEach(([permission, value]) => {
    setter(`${keyPrefix}${permission}`, value);
  });
};

export const setEntityPermissionsFromPermissionLevel = ({
  permissionLevel,
  keyPrefix,
  permissionsToLabelsMap = EntityLevelPermissionsToLabelsMap,
  setter,
}: {
  permissionLevel: `${CustomRolePermissionLevel}`;
  keyPrefix: string;
  permissionsToLabelsMap?: typeof EntityLevelPermissionsToLabelsMap;
  setter: (key: string, value: string) => void;
}) => {
  Object.keys(permissionsToLabelsMap).forEach((permission) => {
    setter(`${keyPrefix}${permission}`, permissionLevel);
  });
};
